<script setup lang="ts">
import { AnnuitiesPremium, policyIsNotType, policyIsType, PolicyType } from "@/entities/premium";
import { formatCurrency } from "@/composables/formatCurrency";
import { Quote } from "@/entities/quote";
import { PaymentFrequency } from "@/stores/pensionOptions";
import { useConfigStore } from "@/stores/config";
import { LINKS } from "@/constants/links";
import { getCompanyIdBySlug } from "@/utils";
import { SCOTTISH_WIDOWS_ERROR_STATUS_CODE, STANDARD_LIFE_PROCESSING_DELAY_MESSAGE } from "@/constants/companyNames";
import ExpiryMessage from "./ExpiryMessage.vue";
import ProviderWarningBanner from "./Results/Premium/Shared/ProviderWarningBanner.vue";

const config = useConfigStore();

const props = defineProps<{
    quotation: AnnuitiesPremium;
    quote: Quote;
    isOpen?: boolean;
    quoteNumber: string;
    showMoreInfoButton: boolean;
} >();

const emit = defineEmits<{
    (on: "update:accordion", value: number): void
    (on: "apply", slug: AnnuitiesPremium): void
    (on: "amsGetGuaranteedQuotes"): void
    (on: "reQuote"): void
}>();

function applyClick() {
    emit("apply", props.quotation);
}

const getErrorStatusCode = (name: string, statusCode: number): string => {
    switch (true) {
        case (statusCode >= 200 && statusCode <= 299):
            return `<h3 style="font-size: 24px">Not Available</h3><h6>${name} was unable to return a quote.</h6>`;
        case (statusCode >= 400 && statusCode <= 499):
            return "<h6>" + name + " could not return a quote at this time. Please try later.</h6>";
        case (statusCode >= 500 && statusCode <= 599):
            return "<h6>" + name + " could not return a quote at this time. Please try later.</h6>";
        case (statusCode === SCOTTISH_WIDOWS_ERROR_STATUS_CODE):
            // eslint-disable-next-line max-len, vue/max-len
            return "<h6>Scottish Widows is unable to return an online quote. If you would like a guaranteed quote to compare, please call Annuity Ready on <a href='tel:08003265479'>0800 326 5479</a>. </h6>";
        default:
            return "<h6>" + name + " could not return a quote.</h6>";
    }
};

</script>

<template>
    <div
        class="card single-result-card"
        :class="!quotation.policy.error && quotation.policy.type === PolicyType.Indicative ? 'indicative-quote' : ''"
    >
        <div
            :id="'heading' + quotation.premiumId"
            class="card-header-res"
        >
            <div class="row row-results-height">
                <div
                    class="col-xl-2 col-lg-3 middle text-center"
                    :class="quotation.policy.error ? 'col-3' : 'col-6'"
                >
                    <img
                        class="img-fluid img-logo-height"
                        :src="`${config.cdnUrl}/logos/annuities/${quotation.company.slug}.svg`"
                        :alt="quotation.company.name"
                        height="125px"
                        width="50px"
                    >
                </div>
                <template v-if="policyIsType(quotation, PolicyType.Error)">
                    <div
                        class="col-xl-10 col-lg-9 col-md-12 my-auto text-center chalet align-items-center bg-income"
                    >
                        <div
                            id="quoteContainer"
                            v-html="getErrorStatusCode(
                                quotation.company.name,
                                quotation.policy.statusCode ?? 0
                            )"
                        />
                    </div>
                </template>
                <template v-else>
                    <div
                        v-if="quotation.policy.totalAnnualPension!== 0"
                        class="col-xl-7 col-lg-9 col-md-12 my-auto chalet bg-income"
                    >
                        <div
                            class="row m-0"
                            :class="quote.product.paymentFrequency === PaymentFrequency.Yearly ? 'justify-content-md-center' : ''"
                        >
                            <h2 class="font-weight-bold line-height col-12 col-lg-6 text-center text-lg-left">
                                <template v-if="quote.product.paymentFrequency === PaymentFrequency.Monthly">
                                    {{ formatCurrency(quotation.policy.totalAnnualPension ? quotation.policy.totalAnnualPension / 12 : 0, 2).split(".")[0] }}<span class="pence">.{{ formatCurrency(quotation.policy.totalAnnualPension ? quotation.policy.totalAnnualPension / 12 : 0, 2).split(".")[1] }}</span>
                                    <small class="projected-2"> per month</small>
                                </template>
                                <template v-if="quote.product.paymentFrequency === PaymentFrequency.Quarterly">
                                    {{ formatCurrency(quotation.policy.totalAnnualPension ? quotation.policy.totalAnnualPension / 4 : 0, 2).split(".")[0] }}<span class="pence">.{{ formatCurrency(quotation.policy.totalAnnualPension ? quotation.policy.totalAnnualPension / 4 : 0, 2).split(".")[1] }}</span>
                                    <small class="projected-2"> per quarter</small>
                                </template>
                                <template v-if="quote.product.paymentFrequency === PaymentFrequency.HalfYearly">
                                    {{ formatCurrency(quotation.policy.totalAnnualPension ? quotation.policy.totalAnnualPension / 2 : 0, 2).split(".")[0] }}<span class="pence">.{{ formatCurrency(quotation.policy.totalAnnualPension ? quotation.policy.totalAnnualPension / 2 : 0, 2).split(".")[1] }}</span>
                                    <small class="projected-2"> per half year</small>
                                </template>
                                <template v-if="quote.product.paymentFrequency === PaymentFrequency.Yearly">
                                    {{ formatCurrency(quotation.policy.totalAnnualPension ? quotation.policy.totalAnnualPension : 0, 2).split(".")[0] }}<span class="pence">.{{ formatCurrency(quotation.policy.totalAnnualPension ? quotation.policy.totalAnnualPension : 0, 2).split(".")[1] }}</span>
                                    <small class="projected-2"> per year</small>
                                </template>
                            </h2>
                            <div
                                v-if="quote.product.paymentFrequency !== 'annually'"
                                class="col-12 col-lg-6 font-weight-bold text-center align-items-center text-lg-left border-left pl-0 pl-lg-4 pl-xl-5"
                            >
                                <p>Annual income<br>{{ formatCurrency(quotation.policy.totalAnnualPension ? quotation.policy.totalAnnualPension : 0, 2) }}</p>
                            </div>
                        </div>
                        <ExpiryMessage
                            v-if="policyIsNotType(quotation, PolicyType.Error)"
                            :is-expired="quotation.policy.isExpired || false"
                            :expiry-date="quotation.policy.expiryDate"
                            responsive-classes="d-none d-xl-block"
                        />
                    </div>
                    <div
                        v-if="quotation.quoteGeneratedBy === 'AMS' || (policyIsNotType(quotation, PolicyType.Error) && quotation.policy.isExpired)"
                        class="col-12 col-xl-3"
                    >
                        <button
                            :id="'btn-guaranteed-quotes_'+ quotation.premiumId"
                            type="button"
                            class="col-12 col-sm-10 col-lg-8 col-xl-12 mx-auto more_info btn btn-info mt-4 mt-lg-1 mb-3 middle collapsed"
                            @click="policyIsNotType(quotation, PolicyType.Error) && quotation.policy.isExpired
                                ? emit('reQuote') : emit('amsGetGuaranteedQuotes')"
                        >
                            Get guaranteed quotes
                        </button>
                    </div>
                    <div
                        v-else
                        class="col-12 col-xl-3"
                    >
                        <button
                            v-if="props.showMoreInfoButton"
                            :id="'btn-more-info_'+ quotation.premiumId"
                            type="button"
                            class="col-12 col-sm-10 col-lg-8 col-xl-12 mx-auto more_info btn btn-info mt-4 mt-lg-1 mb-3 middle collapsed"
                            :aria-expanded="props.isOpen || false"
                            :aria-controls="'more-info_' + quotation.premiumId"
                            @click="emit('update:accordion', quotation.premiumId)"
                        >
                            {{ props.isOpen ? "Hide Info" : "More Info" }}
                        </button>
                    </div>
                </template>
            </div>
            <template v-if="!quotation.policy.error">
                <div
                    v-if="policyIsType(quotation, PolicyType.Indicative) && quotation.quoteGeneratedBy !== 'AMS'"
                    class="indicative-quote-warning"
                >
                    <div class="indicative-quote-label">
                        Indicative Quote
                    </div>
                    <p>
                        Based on the information you have provided, {{ quotation.company.name }} have only been able to provide an indicative quote. To find out how to get a guaranteed quote please click
                        <a
                            data-bs-toggle="collapse"
                            :data-bs-target="'#collapse' + quotation.premiumId"
                            aria-expanded="false"
                            aria-controls="collapseOne"
                        >"More Info".</a>
                    </p>
                </div>
                <div
                    v-if="!quotation.policy.error"
                    :id="'more-info_' + quotation.premiumId"
                    class="moreInfo"
                    :class="props.isOpen ? '' : 'collapse'"
                >
                    <ProviderWarningBanner
                        v-if="props.isOpen && config.standardLifeProcessingDelayBannerEnabled && quotation.company.slug === 'sl'"
                        :message="STANDARD_LIFE_PROCESSING_DELAY_MESSAGE"
                    />
                    <div class="row mx-2">
                        <div class="col-12 documents col-md-5 mt-3 mb-3 bg-light p-4 order-2 order-md-1">
                            <h5 class="results-header">
                                Important documents
                            </h5> <div class="pt-1">
                                <h6 class="chalet blue pt-3 font-weight-bold">
                                    {{ quotation.company.name }} documents
                                </h6> <div
                                    class=" p-0 p-m-auto mx-md-auto"
                                >
                                    <a
                                        :href="`/documents/${quoteNumber}/${quotation.premiumId}/keyfacts`"
                                        target="_blank"
                                        class="download product-summary"
                                    >
                                        Quote (Key facts)
                                    </a>
                                </div> <div
                                    v-if="quotation.documents?.keyFeatures !== null"
                                    class="p-0 pt-3 p-m-auto mx-md-auto"
                                >
                                    <a
                                        target="_blank"
                                        :href="quotation.documents?.keyFeatures"
                                        class="download key-features"
                                    >
                                        Key features
                                    </a>
                                </div> <div class="p-0 pt-3 p-m-auto mx-md-auto">
                                    <a
                                        target="_blank"
                                        :href="quotation.company.privacyUrl"
                                        class="download privacy-policy"
                                    >
                                        Privacy Notice
                                    </a>
                                </div>
                                <div
                                    v-if="quotation.documents?.termsConditions !== null"
                                    class="p-0 pt-3 p-m-auto mx-md-auto"
                                >
                                    <a
                                        target="_blank"
                                        :href="quotation.documents?.termsConditions"
                                        class="download"
                                    >
                                        Terms &amp; conditions
                                    </a>
                                </div>
                            </div>
                            <div class="pt-1">
                                <h6 class="chalet blue pt-4 font-weight-bold">
                                    Annuity Ready documents
                                </h6>
                                <div
                                    v-if="quotation.documents?.termsConditions !== null"
                                >
                                    <a
                                        target="_blank"
                                        :href="LINKS.ANNUITY_TERMS_AND_CONDITIONS"
                                        class="download"
                                    >
                                        Terms &amp; conditions
                                    </a>
                                </div>
                                <div class="pt-3">
                                    <a
                                        target="_blank"
                                        :href="LINKS.ANNUITY_PRIVACY_POLICY"
                                        class="download"
                                    >
                                        Privacy Notice
                                    </a>
                                </div>
                                <div class="pt-3">
                                    <a
                                        target="_blank"
                                        :href="`${LINKS.DEMANDS_AND_NEEDS}/${quoteNumber}`"
                                        class="download"
                                    >
                                        Demands &amp; needs statement
                                    </a>
                                </div>
                                <div class="pt-3">
                                    <a
                                        target="_blank"
                                        :href="LINKS.OUR_SERVICES_LINK"
                                        class="download"
                                    >
                                        Our services
                                    </a>
                                </div>
                            </div>
                        </div> <div class="col-12 col-md-7 pl-md-4 order-1 order-md-2">
                            <h5 class="results-header pt-3">
                                Information about {{ quotation.company.name }}
                            </h5> <p class="pb-4">
                                {{ quotation?.datapoints?.description ? quotation.datapoints.description : '' }}
                            </p> <h5 class="results-header">
                                Next steps:
                            </h5> <div class="col-12 pt-3 body-text">
                                <ul>
                                    <li>Continue to shop around with other providers.</li> <li>
                                        Visit <a
                                            :href="LINKS.MONEY_HELPER_LINK"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >MoneyHelper</a> if you’re still unsure.
                                    </li>
                                    <li>Contact us if you have any questions about your quotes.</li>
                                    <li>
                                        Start your application. Your quotes are valid for a limited time.
                                        Please refer to the quote (key facts) document and terms & conditions
                                        for more details on how to secure this guaranteed rate.
                                    </li>
                                </ul>
                            </div>
                            <a
                                v-if="policyIsType(quotation, PolicyType.Indicative)"
                                class="btn btn-indicative mt-2 mb-md-1"
                                href="tel:08003265479"
                            >
                                Please call Annuity Ready on 0800 326 5479 to get a guaranteed quote
                            </a>
                            <button
                                v-else
                                :id="`pw_proceed_with_${getCompanyIdBySlug(quotation.company.slug)}`"
                                width="100%"
                                tabindex="0"
                                class="btn btn-forward btn-apply mt-2 mb-md-1"
                                @click.prevent="applyClick()"
                            >
                                Proceed with {{ quotation.company.name }}
                            </button>
                        </div>
                    </div>
                </div>
                <ExpiryMessage
                    :is-expired="quotation.policy.isExpired || false"
                    :expiry-date="quotation.policy.expiryDate"
                    responsive-classes="d-xl-none"
                />
            </template>
        </div>
    </div>
</template>

<style scoped>
.single-result-card {
    position: relative;
}

.indicative-quote {
    margin-top: 10px;
}
.indicative-quote:first-child {
    margin-top: 20px;
}

.indicative-quote-label {
    position: absolute;
    top: -13px;
    left: 30px;
    padding: 5px 15px;
    border-radius: 20px;
    background-color: var(--warning-yellow);
    font-size: 14px;
}

.indicative-quote-warning {
    margin-bottom: 15px;
    padding: 10px 15px;
    border-radius: 5px;
    background-color: var(--warning-yellow);
}
</style>
